import { useEffect, useState } from "react";
import { Item } from "../api/Items";
import Button from "../shared-components/Button";
import {
  useCreateWebContentParent,
  useManuallyScrapeWebContent,
} from "../api/ItemsApi";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { useApplicationToast } from "./ApplicationToastProvider";
import AddItemIcon from "../shared-components/icons/AddItem";
import { XIcon } from "../shared-components/icons";
import { ReportSummaryItemApi } from "../api/Reports";

type Props = {
  report: ReportSummaryItemApi;
  webContentParent: Item | undefined;
};

const ButtonManualScrapeWebContent = ({ report, webContentParent }: Props) => {
  const { showLargeToast } = useApplicationToast();
  const [startedScraping, setStartedScraping] = useState(false);
  const [loadedWebContentParent, setLoadedWebContentParent] = useState<
    Item | undefined
  >(webContentParent);

  const { mutate: createParent, data: newWebContentParent } =
    useCreateWebContentParent();

  const {
    initScrape,
    scraperInstalled,
    waitingForScraperReady,
    readyToScrape,
    savedWebContent,
    failedWebContent,
  } = useManuallyScrapeWebContent();

  useEffect(() => {
    if (!loadedWebContentParent) {
      createParent(report.report_uuid);
    }
  }, [loadedWebContentParent, report.report_uuid, createParent]);

  useEffect(() => {
    if (newWebContentParent) {
      setLoadedWebContentParent(newWebContentParent);
    }
  }, [newWebContentParent]);

  useEffect(() => {
    if (savedWebContent && savedWebContent.length > 0) {
      showLargeToast?.({
        timeout: 10000,
        title: "Added!",
        message: `Successfully added web content to the report.\n${
          savedWebContent[savedWebContent.length - 1].url
        }`,
        icon: (
          <div className="bg-green-100 text-green-600 rounded-full p-2">
            <AddItemIcon className="w-6" />
          </div>
        ),
      });
    } else if (failedWebContent && failedWebContent.length > 0) {
      showLargeToast?.({
        timeout: 10000,
        title: "Failed",
        message: `Error adding web content to the report.\n${
          failedWebContent[failedWebContent.length - 1].url
        }`,
        icon: (
          <div className="bg-red-100 text-red-600 rounded-full p-2">
            <XIcon />
          </div>
        ),
      });
    }
  }, [savedWebContent, failedWebContent, showLargeToast]);

  const handleManualWebSearch = () => {
    const externalId = loadedWebContentParent?.external_id;
    if (externalId) {
      initScrape(report.report_uuid, externalId, report.full_name);
      setStartedScraping(true);
    } else {
      console.error("No external_id found for web content parent");
    }
  };

  if (waitingForScraperReady) {
    return <LoadingSpinner />;
  }

  if (startedScraping && readyToScrape && loadedWebContentParent) {
    return <div className="text-xl text-green-500">Ready to scrape</div>;
  }

  if (startedScraping && readyToScrape && !loadedWebContentParent) {
    return (
      <div className="text-xl text-red-500">
        Error: Unable to load web content parent item
      </div>
    );
  }

  if (startedScraping && loadedWebContentParent && !readyToScrape) {
    return (
      <div className="text-xl text-red-500">
        Error: Unable to connect to google scrape extension
      </div>
    );
  }

  return (
    <Button
      disabled={!scraperInstalled}
      text="Manual Web Search"
      onClick={handleManualWebSearch}
      className=""
    />
  );
};

export default ButtonManualScrapeWebContent;
